import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Gauge, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getLatentCap, runLatentCap, setLatentCapConfig } from "@/lib/api";
import { InfoHint } from "@/components/InfoHint";

const fmt = (v) => (v > 0 ? `+${v.toFixed(2)}` : Number(v).toFixed(2));

export const LatentCapBlock = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getLatentCap().then(setD).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      setD(await runLatentCap());
      toast.success("Latente riletto dal banco");
    } catch { toast.error("Lettura non riuscita"); }
    setBusy(false);
  };

  const flip = async () => {
    const mode = d?.mode === "chiudi" ? "avvisa" : "chiudi";
    try {
      await setLatentCapConfig({ mode });
      setD((p) => ({ ...p, mode }));
      toast.success(mode === "chiudi"
        ? "Modo «chiudi»: oltre il tetto la posizione viene chiusa a mercato"
        : "Modo «avvisa»: nessuna chiusura automatica, decidi tu");
    } catch { toast.error("Cambio modo non riuscito"); }
  };

  if (!d) return null;
  const rows = (d.positions || []).filter((p) => !p.error);
  const errs = (d.positions || []).filter((p) => p.error);
  const worst = rows.filter((p) => p.breached);
  return (
    <div className="rounded-md border border-border/60 bg-background/40 p-2.5 space-y-2"
      data-testid="latent-cap-block">
      <div className="flex flex-wrap items-center gap-2">
        <Gauge className="h-3.5 w-3.5 text-amber-300" />
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Tetto sul latente · {d.multiple}x il rischio dichiarato
        </span>
        <InfoHint testId="info-latent-cap" text={d.rule || ""} />
        <Badge variant="outline"
          className={`text-[10px] cursor-pointer ${d.mode === "chiudi"
            ? "border-rose-500/50 text-rose-300" : "border-border text-muted-foreground"}`}
          onClick={flip} data-testid="latent-cap-mode">
          modo: {d.mode}
        </Badge>
        <Button size="sm" variant="ghost" className="h-6 px-2 ml-auto" onClick={run}
          disabled={busy} data-testid="latent-cap-run">
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {!rows.length && (
        <p className="text-[11px] text-muted-foreground" data-testid="latent-cap-empty">
          Nessuna posizione aperta da misurare.
        </p>
      )}

      {rows.map((p) => (
        <div key={`${p.venue}-${p.id}`}
          className={`flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded border px-2 py-1.5 ${
            p.breached ? "border-rose-500/50 bg-rose-500/[0.07]" : "border-border/50"}`}
          data-testid={`latent-cap-row-${p.venue}-${p.id}`}>
          <span className="text-[11px] text-white/80">{p.market} {p.side}</span>
          <span className="text-[10px] text-muted-foreground">{p.venue}</span>
          <span className={`font-mono text-[11px] ${p.latentUsd < 0 ? "text-rose-300" : "text-emerald-300"}`}
            data-testid={`latent-cap-latent-${p.id}`}>
            latente {fmt(p.latentUsd)}$
          </span>
          <span className="font-mono text-[10px] text-muted-foreground">
            rischio dichiarato {p.riskUsd}$ · <b className={p.breached ? "text-rose-300" : ""}>{p.times}x</b>
          </span>
          <span className="basis-full font-mono text-[10px] text-muted-foreground/80">
            stop {p.stop ?? "—"} ({p.stopSource}) · rischio da: {p.riskSource}
          </span>
          {p.action && (
            <span className="basis-full text-[10px] text-amber-300/90"
              data-testid={`latent-cap-action-${p.id}`}>
              → {p.action}
            </span>
          )}
        </div>
      ))}

      {errs.map((e, i) => (
        <p key={i} className="font-mono text-[10px] text-rose-300/80"
          data-testid="latent-cap-error">
          {e.venue} {e.accountId ?? ""}: {e.error}
        </p>
      ))}

      <p className="text-[10px] text-muted-foreground/70">
        {worst.length
          ? `${worst.length} posizione/i oltre il tetto.`
          : "Tutte dentro il rischio dichiarato."}
        {" "}{d.esecutore === false && "Questo ambiente non esegue: legge e dichiara, non tocca."}
      </p>
    </div>
  );
};

export default LatentCapBlock;
